import { useMemo } from 'react';
import { D, type ChatBalanceEntry } from '@wave/shared';
import { useUsdtRates } from '../../api/hooks';
import { EChart, type EChartsOption } from '../EChart';
import { useIsNarrow } from '../../lib/useMediaQuery';
import { BASE_GRID, CHART_COLORS, tooltipFit, valueAxisLabel } from './ChartsPanel';

/**
 * Риск концентрации: топ-5 чатов по |net USDT| сейчас (live-данные, не история)
 * + доля топ-5 от совокупного объёма |net| всех чатов.
 */
export function ConcentrationChart({ liveChats }: { liveChats: ChatBalanceEntry[] }) {
  const { data: rates } = useUsdtRates();
  const narrow = useIsNarrow();

  const { top, sharePct } = useMemo(() => {
    const ratesMap = new Map((rates ?? []).map((r) => [r.ccy, r.rate]));
    // Концентрация — риск на контрагентах; наши кошельки не контрагенты.
    const chatsOnly = liveChats.filter((c) => c.source !== 'tron');
    const rows: { title: string; net: ReturnType<typeof D> }[] = [];
    for (const chat of chatsOnly) {
      let net = D(0);
      let missing = false;
      for (const b of chat.balances) {
        const rate = ratesMap.get(b.ccy);
        if (!rate || D(rate).lte(0)) {
          missing = true;
          break;
        }
        net = net.plus(D(b.amount).dividedBy(D(rate)));
      }
      if (!missing && !net.isZero()) rows.push({ title: chat.title, net });
    }
    rows.sort((a, b) => b.net.abs().comparedTo(a.net.abs()));
    const total = rows.reduce((acc, r) => acc.plus(r.net.abs()), D(0));
    const top5 = rows.slice(0, 5);
    const topSum = top5.reduce((acc, r) => acc.plus(r.net.abs()), D(0));
    return {
      top: top5.reverse(),
      sharePct: total.isZero() ? null : topSum.dividedBy(total).times(100).toDecimalPlaces(0).toString(),
    };
  }, [liveChats, rates]);

  const option = useMemo<EChartsOption>(
    () => ({
      animation: false,
      grid: narrow ? { left: 96, right: 12, top: 16, bottom: 32 } : { ...BASE_GRID, left: 160 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...tooltipFit(narrow),
        formatter: barTooltip(top.map((r) => r.title)),
      },
      xAxis: { type: 'value', axisLabel: valueAxisLabel(narrow), splitLine: { lineStyle: { color: CHART_COLORS.grid } } },
      yAxis: {
        type: 'category',
        data: top.map((r) => catLabel(r.title, narrow)),
        axisLabel: { color: CHART_COLORS.text, ...(narrow ? { fontSize: 11 } : {}) },
      },
      series: [
        {
          type: 'bar',
          data: top.map((r) => ({
            value: r.net.toDecimalPlaces(2).toNumber(),
            itemStyle: { color: r.net.gt(0) ? CHART_COLORS.pos : CHART_COLORS.neg },
          })),
          barMaxWidth: 18,
        },
      ],
    }),
    [top, narrow],
  );

  if (top.length === 0)
    return <div className="flex h-40 items-center justify-center text-sm text-text2">Нет чатов с рассчитанным net.</div>;
  return (
    <div>
      {sharePct !== null && (
        <div className="mb-2 text-xs text-text2">
          Топ-5 чатов держат <span className="font-semibold text-text">{sharePct}%</span> совокупного
          объёма |net| — концентрация на крупных контрагентах.
        </div>
      )}
      <EChart option={option} height={Math.max(180, top.length * 34 + 80)} />
    </div>
  );
}

/** Подпись категории: на телефоне короче — поле под подписи уже (полное имя — в тултипе). */
function catLabel(title: string, narrow: boolean): string {
  const max = narrow ? 12 : 24;
  return title.length > max ? `${title.slice(0, max)}…` : title;
}

/** Тултип бара: полное имя чата + значение (подпись оси может быть обрезана). */
function barTooltip(titles: string[]) {
  return (params: unknown) => {
    const p = (params as Array<{ dataIndex: number; value: number; marker: string }>)[0];
    if (!p) return '';
    const v = p.value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    return `${titles[p.dataIndex] ?? ''}<br/>${p.marker} <b>${p.value > 0 ? '+' : ''}${v}</b> USDT`;
  };
}
